import React from 'react';
import { Box, Container, Typography, Card, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import BarChartIcon from '@mui/icons-material/BarChart';
import BoltIcon from '@mui/icons-material/Bolt';
import EmailIcon from '@mui/icons-material/Email';
import CheckIcon from '@mui/icons-material/Check';

const FeaturesSection = () => {
  const features = [
    {
      icon: SearchIcon,
      title: 'Flexible Search Engine',
      description:
        'Search that understands English, Chinese, German, and more — including keywords and Boolean logic. Search with complete freedom.',
      items: [
        'Natural language processing',
        'Boolean search capabilities',
        { text: 'Search through open web signals', chip: 'no extension required' },
      ],
    },
    {
      icon: FilterListIcon,
      title: 'Smart Annotations',
      description:
        'Fine-tune your search results with intelligent filters that understand context and nuance. Find exactly the right candidates with precision targeting.',
      items: [
        { text: 'Diversity & inclusion filters', chips: ['Army Veterans', 'Gender'] },
        { text: 'Executive level targeting', chips: ['CxO', 'Director and Above'] },
        { text: 'Industry expertise matching', chips: ['Fintech', 'Gaming', 'Banking'] },
      ],
    },
    {
      icon: BarChartIcon,
      title: 'Deep Candidate Insights',
      description:
        'AI-powered analysis creates comprehensive profiles with career narratives and competency assessments.',
      items: [
        { text: 'Career milestone analysis', chips: ['Executive Summary', 'Role Recommendations'] },
        'Social footprint aggregation',
        { text: 'Hidden indicators tracking', chips: ['Stability', 'Mobility', 'Progression'] },
      ],
    },
    {
      icon: BoltIcon,
      title: 'AI-driven Assessment',
      description:
        'Comprehensive persona evaluation across multiple dimensions with intelligent insights and actionable recommendations.',
      items: [
        'Assess on functional, behavioral & culture fit',
        { text: 'Assess base competencies', chips: ['Hypothetical', 'Functional'] },
        "Quantitative reasoning on what works and doesn't",
        'AI-powered recommendations',
      ],
    },
    {
      icon: EmailIcon,
      title: 'Contact & Prospecting Efficiency',
      description:
        'Reduce prospecting time from days to hours with enriched contact details and CRM-ready profiles.',
      items: [{ text: 'Contact enrichment', chips: ['Phone', 'Email'] }],
    },
  ];

  const chipStyles = {
    blue: { bgcolor: '#e0e7ff', color: '#3730a3' },
    purple: { bgcolor: '#f3e8ff', color: '#6b21a8' },
    orange: { bgcolor: '#ffedd5', color: '#9a3412' },
    green: { bgcolor: '#dcfce7', color: '#065f46' },
  };

  return (
    <Box id="features" component="section" sx={{ py: { xs: 8, md: 12 }, bgcolor: '#ffffff' }}>
      <Container maxWidth="lg">
        {/* ---------- HEADER ---------- */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            sx={{
              fontSize: { xs: '2.2rem', md: '2.75rem' },
              fontWeight: 800,
              color: '#111827',
              mb: 2,
            }}
          >
            Platform Capabilities
          </Typography>

          <Typography
            sx={{
              fontSize: '1.125rem',
              color: '#6b7280',
              maxWidth: 820,
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            Powered by AI and the open web, Getlista transforms how you discover,
            analyze, and connect with experienced hires and executive talent.
          </Typography>
        </Box>

        {/* ---------- CARDS ---------- */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
          }}
        >
          {features.map((feature, index) => (
            <Card
              key={feature.title}
              sx={{
                width: {
                  xs: '100%',
                  md: index === 4 ? '100%' : 'calc(50% - 16px)',
                },
                p: 4,
                borderRadius: 3,
                border: '1px solid #e5e7eb',
                boxShadow: 'none',
                transition: 'all 0.25s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.06)',
                },
              }}
            >
              {/* ICON */}
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  bgcolor: '#ecfdf5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <feature.icon sx={{ fontSize: 28, color: '#16a34a' }} />
              </Box>

              {/* TITLE */}
              <Typography
                sx={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: '#111827',
                  mb: 1.5,
                }}
              >
                {feature.title}
              </Typography>

              {/* DESCRIPTION */}
              <Typography
                sx={{
                  fontSize: '0.95rem',
                  color: '#6b7280',
                  lineHeight: 1.7,
                  mb: 3,
                }}
              >
                {feature.description}
              </Typography>

              {/* LIST */}
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {feature.items.map((item, idx) => (
                  <Box
                    component="li"
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      mb: 1.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 16, color: '#16a34a', mt: '4px' }} />

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '0.9rem', color: '#111827' }}>
                        {typeof item === 'string' ? item : item.text}
                      </Typography>

                      {typeof item === 'object' && item.chip && (
                        <Chip
                          label={item.chip}
                          size="small"
                          sx={{
                            ...chipStyles.green,
                            height: 22,
                            fontSize: '0.72rem',
                          }}
                        />
                      )}

                      {typeof item === 'object' &&
                        item.chips &&
                        item.chips.map((chip, i) => (
                          <Chip
                            key={chip}
                            label={chip}
                            size="small"
                            sx={{
                              ...(Object.values(chipStyles)[i % 3]),
                              height: 22,
                              fontSize: '0.72rem',
                            }}
                          />
                        ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Card>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default FeaturesSection;
